import { useState } from 'react';

import { DateInput, MonthInput } from '../ui';
import { cn } from '../../utils/cn';
import { currentMonthKey, monthRange } from '../../utils/money';

export interface PayrollRange {
    from: string;
    to: string;
}

export function defaultPayrollRange(): PayrollRange {
    return monthRange(currentMonthKey());
}

interface PayrollRangePickerProps {
    value: PayrollRange;
    onChange: (range: PayrollRange) => void;
}

// A month by default; "Tuỳ chọn" switches to any from/to range.
export function PayrollRangePicker({ value, onChange }: PayrollRangePickerProps) {
    const isWholeMonth = value.from.slice(0, 7) === value.to.slice(0, 7)
        && monthRange(value.from.slice(0, 7)).from === value.from
        && monthRange(value.from.slice(0, 7)).to === value.to;
    const [mode, setMode] = useState<'month' | 'custom'>(isWholeMonth ? 'month' : 'custom');

    const switchMode = (next: 'month' | 'custom') => {
        setMode(next);
        if (next === 'month') onChange(monthRange(value.from.slice(0, 7) || currentMonthKey()));
    };

    return (
        <div className="flex flex-wrap items-center gap-2">
            <div className="inline-flex rounded-lg border border-ink-200 bg-white p-0.5" role="group" aria-label="Kiểu thời gian">
                {(['month', 'custom'] as const).map(option => (
                    <button
                        key={option}
                        type="button"
                        aria-pressed={mode === option}
                        onClick={() => switchMode(option)}
                        className={cn(
                            'min-h-8 rounded px-3 text-sm transition-colors',
                            mode === option ? 'bg-ink-100 font-semibold text-ink-900' : 'text-ink-600 hover:text-ink-900',
                        )}
                    >
                        {option === 'month' ? 'Theo tháng' : 'Tuỳ chọn'}
                    </button>
                ))}
            </div>
            {mode === 'month' ? (
                <MonthInput
                    aria-label="Tháng"
                    value={value.from.slice(0, 7)}
                    onChange={month => month && onChange(monthRange(month))}
                    clearable={false}
                    className="w-36"
                />
            ) : (
                <>
                    <DateInput
                        aria-label="Từ ngày"
                        value={value.from}
                        max={value.to}
                        onChange={from => from && onChange({ ...value, from })}
                        clearable={false}
                        className="w-40"
                    />
                    <DateInput
                        aria-label="Đến ngày"
                        value={value.to}
                        min={value.from}
                        onChange={to => to && onChange({ ...value, to })}
                        clearable={false}
                        className="w-40"
                    />
                </>
            )}
        </div>
    );
}
